import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Mail, Phone, MapPin, ArrowUpRight } from "lucide-react";
import { useI18n } from "../lib/i18n";
import { BRAND, WA_LINK } from "../lib/content";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function Contact() {
  const { t } = useI18n();

  return (
    <section className="py-16 md:py-24" data-testid="contact-page">
      <div className="max-w-7xl mx-auto px-5 md:px-8 grid md:grid-cols-12 gap-12">
        <div className="md:col-span-5">
          <div className="overline">{t("contact.overline")}</div>
          <h1 className="mt-5 text-5xl md:text-7xl leading-[1.02]">
            {t("contact.title")}
          </h1>
          <p className="mt-6 text-[#5C5042] text-lg max-w-md">
            {t("contact.subtitle")}
          </p>
          <ContactChannels />
        </div>

        <ContactForm />
      </div>
    </section>
  );
}

function ContactChannels() {
  const { lang } = useI18n();

  return (
    <div className="mt-10 space-y-6">
      <a
        href={WA_LINK}
        target="_blank"
        rel="noreferrer"
        data-testid="contact-whatsapp"
        className="flex items-center gap-4 group"
      >
        <div className="h-12 w-12 flex items-center justify-center bg-[#25D366] text-white">
          <Phone size={18} strokeWidth={1.4} />
        </div>
        <div>
          <div className="overline text-[#5C5042]">WhatsApp</div>
          <div className="font-serif-display text-xl group-hover:text-[#6B4A2B]">
            {BRAND.whatsapp}
          </div>
        </div>
      </a>

      <a
        href={`mailto:${BRAND.email}`}
        data-testid="contact-email"
        className="flex items-center gap-4 group"
      >
        <div className="h-12 w-12 flex items-center justify-center bg-[#6B4A2B] text-[#F9F6F0]">
          <Mail size={18} strokeWidth={1.4} />
        </div>
        <div>
          <div className="overline text-[#5C5042]">Email</div>
          <div className="font-serif-display text-xl group-hover:text-[#6B4A2B]">
            {BRAND.email}
          </div>
        </div>
      </a>

      <div className="flex items-start gap-4">
        <div className="h-12 w-12 flex items-center justify-center bg-[#2E5B6D] text-[#F9F6F0] shrink-0">
          <MapPin size={18} strokeWidth={1.4} />
        </div>
        <div>
          <div className="overline text-[#5C5042]">
            {lang === "fr" ? "Adresse" : "Address"}
          </div>
          <div className="font-serif-display text-xl">Grand-Popo, Bénin</div>
          <a
            href={BRAND.googleMaps}
            target="_blank"
            rel="noreferrer"
            data-testid="contact-open-maps"
            className="mt-1 inline-flex items-center gap-2 text-xs tracking-[0.22em] uppercase text-[#6B4A2B] border-b border-[#6B4A2B]/40 pb-0.5 hover:border-[#6B4A2B]"
          >
            Google Maps <ArrowUpRight size={12} />
          </a>
        </div>
      </div>
    </div>
  );
}

function ContactForm() {
  const { t, lang } = useI18n();
  const [form, setForm] = useState({ name: "", email: "", message: "" });
  const [sending, setSending] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (sending) return;
    setSending(true);
    try {
      await axios.post(`${API}/contact`, { ...form, language: lang });
      toast.success(t("contact.sent"));
      setForm({ name: "", email: "", message: "" });
    } catch (err) {
      const detail = err?.response?.data?.detail || t("booking.error");
      toast.error(String(detail));
    } finally {
      setSending(false);
    }
  };

  return (
    <form
      onSubmit={submit}
      className="md:col-span-7 bg-white p-8 md:p-12 border border-[#6B4A2B]/10"
      data-testid="contact-form"
    >
      <h2 className="font-serif-display text-3xl mb-8">
        {t("contact.form_title")}
      </h2>
      <div className="grid md:grid-cols-2 gap-x-8 gap-y-6">
        <label className="block">
          <span className="overline block mb-2 text-[#5C5042]">
            {t("contact.name")}
          </span>
          <input
            required
            data-testid="contact-name"
            className="paper-input"
            value={form.name}
            onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))}
          />
        </label>
        <label className="block">
          <span className="overline block mb-2 text-[#5C5042]">
            {t("contact.email")}
          </span>
          <input
            required
            type="email"
            data-testid="contact-email-input"
            className="paper-input"
            value={form.email}
            onChange={(e) => setForm((f) => ({ ...f, email: e.target.value }))}
          />
        </label>
        <label className="block md:col-span-2">
          <span className="overline block mb-2 text-[#5C5042]">
            {t("contact.message")}
          </span>
          <textarea
            required
            rows={6}
            data-testid="contact-message"
            className="paper-input resize-none"
            value={form.message}
            onChange={(e) => setForm((f) => ({ ...f, message: e.target.value }))}
          />
        </label>
      </div>
      <button
        type="submit"
        disabled={sending}
        data-testid="contact-submit"
        className="btn-primary mt-10"
      >
        {sending ? t("booking.submitting") : t("contact.send")}
        {!sending && <ArrowUpRight size={14} />}
      </button>
    </form>
  );
}
